import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactPlayer from "react-player";
import { 
  Card, 
  Container, 
  Row, 
  Col, 
  ProgressBar, 
  Badge, 
  Spinner, 
  Alert, 
  Button 
} from "react-bootstrap";
import UserContext from "../../../contexts/UserContext";
import { 
  getVideoPlayerData, 
  checkVideoAccess, 
  startVideo,
  updateProgress,
  completeVideo 
} from "../../../services/TrainingService";
import TrainingAccessControl from "./TrainingAccessControl";

const VideoPlayer = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const playerRef = useRef(null);
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoStarted, setVideoStarted] = useState(false);
  const [lastProgressUpdate, setLastProgressUpdate] = useState(0);
  const [totalWatchTime, setTotalWatchTime] = useState(0);
  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    if (videoId) {
      loadVideoData();
    }
  }, [videoId]);

  // Definir posição inicial e duração quando videoData é carregado
  useEffect(() => {
    if (videoData) {
      // Definir duração se disponível
      if (videoData.duration_seconds && videoData.duration_seconds > 0 && (!duration || duration === 0)) {
        setDuration(videoData.duration_seconds);
        console.log("Setting duration from videoData:", videoData.duration_seconds);
      }
      
      // Definir posição inicial
      if (videoData.progress?.last_position_seconds > 0 && videoData.duration_seconds > 0) {
        setPlayed(videoData.progress.last_position_seconds / videoData.duration_seconds);
      }
      
      // Definir tempo assistido
      if (videoData.progress?.total_watch_time) {
        setTotalWatchTime(videoData.progress.total_watch_time);
      }
    }
  }, [videoData, duration]);

  const loadVideoData = async () => {
    try {
      setLoading(true);
      
      console.log("Loading video data for videoId:", videoId);
      
      try {
        // Verificar acesso ao vídeo
        const accessData = await checkVideoAccess(videoId);
        console.log("Access data:", accessData);
        
        if (!accessData.is_unlocked) {
          setError("Você não tem acesso a este vídeo");
          return;
        }

        // Carregar dados do player
        const playerData = await getVideoPlayerData(videoId);
        console.log("Player data:", playerData);
        setVideoData(playerData);
      } catch (apiError) {
        console.warn("API specific endpoints failed, falling back to basic video data:", apiError);
        
        // Fallback: carregar dados básicos do vídeo
        const { getTrainingVideo } = await import("../../../services/TrainingService");
        const basicVideoData = await getTrainingVideo(videoId);
        console.log("Basic video data:", basicVideoData);
        
        // Simular estrutura esperada para compatibilidade
        const fallbackData = {
          video_id: basicVideoData.id,
          title: basicVideoData.title,
          description: basicVideoData.description,
          video_url: basicVideoData.external_url,
          video_type: basicVideoData.video_type,
          duration_seconds: basicVideoData.duration_seconds || 0,
          thumbnail_url: basicVideoData.thumbnail_url,
          category: {
            id: basicVideoData.category,
            name: basicVideoData.category_name || "Categoria"
          },
          progress: {
            progress_percent: 0,
            last_position_seconds: 0,
            completed: false,
            completion_threshold: 80.0
          }
        };
        
        setVideoData(fallbackData);
      }
    } catch (error) {
      console.error("Erro ao carregar vídeo:", error);
      setError("Erro ao carregar dados do vídeo");
    } finally {
      setLoading(false);
    }
  };

  const handlePlayClick = () => {
    setPlaying(true);
  };

  const handlePauseClick = () => {
    setPlaying(false);
  };

  const handleProgress = (state) => {
    setPlayed(state.played);
    setLoaded(state.loaded);
  };

  const handleDuration = (duration) => {
    if (duration && duration > 0) {
      setDuration(duration);
      console.log("Video duration set:", duration);
    } else if (videoData?.duration_seconds && videoData.duration_seconds > 0) {
      // Fallback para duração da API se ReactPlayer não conseguir detectar
      setDuration(videoData.duration_seconds);
      console.log("Using API duration:", videoData.duration_seconds);
    }
  };

  const handleProgressUpdate = useCallback(async () => {
    // Validações básicas mais flexíveis
    if (!videoData || !videoStarted) {
      console.log("Progress update skipped - missing videoData or videoStarted:", { videoData: !!videoData, videoStarted });
      return;
    }

    // Permitir atualização mesmo sem duração definida (será 0 inicialmente)
    const videoDuration = duration > 0 ? duration : (videoData?.duration_seconds || 0);
    
    // Garantir que os valores são válidos
    const currentProgress = isNaN(played) ? 0 : Math.max(0, Math.min(100, played * 100));
    const currentPosition = isNaN(played * videoDuration) ? 0 : Math.max(0, played * videoDuration);
    const watchTime = isNaN(totalWatchTime) ? 0 : Math.max(0, totalWatchTime);
    
    // Debug dos valores antes do envio
    console.log("Progress update values before API call:", {
      currentProgress,
      currentPosition, 
      watchTime,
      totalWatchTime,
      played,
      videoDuration,
      lastProgressUpdate
    });
    
    // Reduzir frequência mínima para 0.5% e permitir primeira atualização
    const progressDifference = Math.abs(currentProgress - lastProgressUpdate);
    if (progressDifference < 0.5 && lastProgressUpdate > 0) {
      console.log("Progress update skipped - insufficient change:", { currentProgress, lastProgressUpdate, difference: progressDifference });
      return;
    }

    // Remover validação que impedia atualizações no início do vídeo
    console.log("Progress update conditions met:", {
      videoData: !!videoData,
      videoStarted,
      duration: videoDuration,
      currentProgress,
      currentPosition,
      watchTime,
      progressDifference
    });

    try {
      // Usar video_id se disponível, senão usar videoId do parâmetro da URL
      const videoIdToUse = videoData.video_id || videoData.id || videoId;
      
      console.log("Sending progress update:", {
        videoId: videoIdToUse,
        progress: currentProgress,
        position: currentPosition,
        watchTime: watchTime,
        played: played,
        duration: videoDuration
      });
      
      await updateProgress(
        videoIdToUse,
        currentProgress,
        Math.floor(currentPosition),
        Math.floor(watchTime)
      );
      setLastProgressUpdate(currentProgress);
      console.log("Progress updated successfully");
    } catch (error) {
      console.error("Erro ao atualizar progresso:", error);
    }
  }, [videoData, videoStarted, duration, played, totalWatchTime, lastProgressUpdate, videoId]);

  // Atualizar progresso a cada 5 segundos e quando há mudanças significativas
  useEffect(() => {
    const interval = setInterval(() => {
      if (playing && videoData && videoStarted) {
        console.log("Interval update - conditions:", { playing, videoData: !!videoData, videoStarted });
        const currentTime = Date.now();
        if (startTime) {
          const sessionWatchTime = (currentTime - startTime) / 1000;
          console.log("Adding session watch time:", sessionWatchTime, "seconds");
          setTotalWatchTime(prev => {
            const newTotal = prev + sessionWatchTime;
            console.log("Total watch time updated:", prev, "→", newTotal);
            return newTotal;
          });
        }
        setStartTime(currentTime);
        handleProgressUpdate();
      } else {
        console.log("Interval skipped - conditions not met:", { playing, videoData: !!videoData, videoStarted });
      }
    }, 5000); // Reduzido de 10s para 5s para atualizações mais frequentes

    return () => clearInterval(interval);
  }, [playing, videoData, videoStarted, startTime, handleProgressUpdate]);

  const handleVideoEnd = async () => {
    try {
      setPlaying(false);
      
      if (videoData && !videoData.progress.completed) {
        try {
          // Usar video_id se disponível, senão usar videoId do parâmetro da URL
          const videoIdToUse = videoData.video_id || videoData.id || videoId;
          await completeVideo(videoIdToUse);
          // Atualizar dados do vídeo para mostrar como completo
          setVideoData(prev => ({
            ...prev,
            progress: {
              ...prev.progress,
              completed: true,
              progress_percent: 100
            }
          }));
        } catch (error) {
          console.error("Erro ao completar vídeo:", error);
        }
      }
    } catch (error) {
      console.error("Erro no handleVideoEnd:", error);
    }
  };

  const handleSeek = (seconds) => {
    if (playerRef.current && playerRef.current.seekTo) {
      try {
        playerRef.current.seekTo(seconds);
      } catch (error) {
        console.error("Erro ao fazer seek:", error);
      }
    } else {
      console.warn("PlayerRef não disponível ou seekTo não é uma função");
    }
  };


  const formatTime = (seconds) => {
    // Validar entrada
    if (isNaN(seconds) || seconds < 0 || !isFinite(seconds)) {
      return "0:00";
    }
    
    const totalSeconds = Math.floor(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getCompletionBadge = () => {
    if (!videoData) return null;
    
    if (videoData.progress.completed) {
      return <Badge bg="success">Vídeo Concluído</Badge>;
    } else if (videoData.progress.progress_percent >= (videoData.progress.completion_threshold || 80)) {
      return <Badge bg="warning">Quase Concluído</Badge>;
    } else if (videoData.progress.progress_percent > 0) {
      return <Badge bg="info">Em Progresso</Badge>;
    } else {
      return <Badge bg="secondary">Não Iniciado</Badge>;
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Carregando vídeo...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => navigate("/training")}>
          Voltar ao Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <TrainingAccessControl>
      <Container className="mt-4">
        {/* Header do Vídeo */}
        <div className="page-header mb-4">
        <div className="d-flex align-items-center mb-2">
          <Button 
            variant="outline-secondary" 
            size="sm" 
            className="me-3"
            onClick={() => navigate(`/training/category/${videoData.category.id}`)}
          >
            <i className="las la-arrow-left"></i> Voltar
          </Button>
          <div className="flex-grow-1">
            <h1 className="mb-0">{videoData?.title}</h1>
            <p className="text-muted mb-0">{videoData?.category.name}</p>
          </div>
          <div className="ms-auto">
            {getCompletionBadge()}
          </div>
        </div>
      </div>

      <Row>
        {/* Player de Vídeo */}
        <Col xs={12} lg={8}>
          <Card className="mb-4">
            <Card.Body className="p-0">
              <div className="video-player-container" style={{ position: "relative", paddingTop: "56.25%" }}>
                {videoData?.video_url ? (
                  <ReactPlayer
                    ref={playerRef}
                    src={videoData.video_url}
                    width="100%"
                    height="100%"
                    style={{ position: "absolute", top: 0, left: 0 }}
                    playing={playing}
                    controls={true}
                    onPlay={() => {
                      console.log("ReactPlayer onPlay");
                      setPlaying(true);
                      setStartTime(Date.now());
                      if (!videoStarted && videoData) {
                        // Usar video_id se disponível, senão usar videoId do parâmetro da URL
                        const videoIdToUse = videoData.video_id || videoData.id || videoId;
                        startVideo(videoIdToUse).catch(err => 
                          console.error("Erro ao iniciar vídeo:", err)
                        );
                        setVideoStarted(true);
                      }
                    }}
                    onPause={() => {
                      console.log("ReactPlayer onPause");
                      setPlaying(false);
                      if (startTime) {
                        const sessionTime = (Date.now() - startTime) / 1000;
                        console.log("onPause - Adding session time:", sessionTime, "seconds");
                        setTotalWatchTime(prev => {
                          const newTotal = prev + sessionTime;
                          console.log("onPause - Total watch time updated:", prev, "→", newTotal);
                          return newTotal;
                        });
                        setStartTime(null);
                      }
                      // Forçar atualização de progresso ao pausar
                      setTimeout(() => handleProgressUpdate(), 100);
                    }}
                    onProgress={handleProgress}
                    onDuration={handleDuration}
                    onEnded={handleVideoEnd}
                    onError={(error) => {
                      console.error("ReactPlayer error:", error);
                      setError(`Erro ao carregar o vídeo: ${error?.message || 'Verifique se a URL está correta'}`);
                    }}
                  />
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100 bg-light">
                    <div className="text-center">
                      <i className="las la-video" style={{ fontSize: "48px" }}></i>
                      <p className="mt-2">Carregando vídeo...</p>
                    </div>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Informações do Vídeo */}
          <Card className="mb-4">
            <Card.Body>
              <h5>{videoData?.title}</h5>
              {videoData?.description && (
                <p className="text-muted">{videoData.description}</p>
              )}
              
              {/* Progresso do Vídeo */}
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>Progresso do Vídeo</span>
                  <span>{Math.round(videoData?.progress?.progress_percent || 0)}%</span>
                </div>
                <ProgressBar 
                  now={videoData?.progress?.progress_percent || 0} 
                  variant={videoData?.progress?.completed ? "success" : "primary"}
                  style={{ height: "8px" }}
                />
                <small className="text-muted">
                  <i className="las la-info-circle"></i>
                  Progresso estimado baseado no tempo de visualização
                </small>
              </div>

              {/* Estatísticas */}
              <Row className="text-center">
                <Col xs={6} md={4}>
                  <div className="stat-item">
                    <h6 className="mb-0">{formatTime(duration || 0)}</h6>
                    <small className="text-muted">Duração</small>
                  </div>
                </Col>
                <Col xs={6} md={4}>
                  <div className="stat-item">
                    <h6 className="mb-0">{formatTime(totalWatchTime || 0)}</h6>
                    <small className="text-muted">Tempo Assistido</small>
                  </div>
                </Col>
                
                <Col xs={6} md={4}>
                  <div className="stat-item">
                    <h6 className="mb-0">{videoData?.progress?.completion_threshold || 80}%</h6>
                    <small className="text-muted">Para Completar</small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col xs={12} lg={4}>
          {/* Controles Rápidos */}
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Controles</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button 
                  variant={playing ? "danger" : "primary"}
                  onClick={() => {
                    if (playing) {
                      handlePauseClick();
                    } else {
                      handlePlayClick();
                    }
                  }}
                  disabled={!videoData?.video_url}
                >
                  <i className={`las ${playing ? 'la-pause' : 'la-play'}`}></i>
                  {playing ? ' Pausar' : ' Reproduzir'}
                </Button>
                
                {videoData?.progress?.last_position_seconds > 0 && (
                  <Button 
                    variant="outline-secondary"
                    onClick={() => {
                      if (playerRef.current && playerRef.current.seekTo) {
                        handleSeek(videoData.progress.last_position_seconds);
                      }
                    }}
                    disabled={!playerRef.current}
                  >
                    <i className="las la-undo"></i>
                    Continuar de onde parou
                  </Button>
                )}
                
                <Button 
                  variant="outline-secondary"
                  onClick={() => {
                    if (playerRef.current && playerRef.current.seekTo) {
                      handleSeek(0);
                    }
                  }}
                  disabled={!playerRef.current}
                >
                  <i className="las la-step-backward"></i>
                  Reiniciar vídeo
                </Button>
                
               
              </div>
            </Card.Body>
          </Card>

          {/* Informações da Categoria */}
          <Card>
            <Card.Header>
              <h6 className="mb-0">Categoria: {videoData?.category.name}</h6>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                Este vídeo faz parte da categoria "{videoData?.category.name}". 
                Complete todos os vídeos para desbloquear a próxima categoria.
              </p>
              
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => navigate(`/training/category/${videoData?.category.id}`)}
              >
                Ver todos os vídeos da categoria
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      </Container>
    </TrainingAccessControl>
  );
};

export default VideoPlayer;